import React, { useEffect, useState } from "react";
import { LOAN } from "../config/apiConfig";
import { GoAlertFill } from "react-icons/go";
import { useForm } from "react-hook-form";
import Input from "../components/inputs/Input";
import CNPJInput from "../components/inputs/CNPJInput";
import PhoneInput from "../components/inputs/PhoneInput";
import { GiFarmTractor } from "react-icons/gi";
import { TbReportMoney } from "react-icons/tb";
import CurrencyInput from "../components/inputs/CurrencyInput";
import SelectInput from "../components/inputs/SelectInput";
import { useQuery, useQueryClient } from "react-query";
import axios from "../config/axiosInstance";
import { useParams } from "react-router-dom";
import DrawerLoading from "../components/DrawerLoading";
import FileUploader from "../components/inputs/FileUploader";
import "./FormPage.css";
import {
  FiArrowRight,
  FiCheckCircle,
  FiFileText,
  FiHome,
  FiLock,
  FiMapPin,
  FiShield,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { BsFillBuildingFill } from "react-icons/bs";
import { FaWandMagicSparkles } from "react-icons/fa6";

const WIZARD_STEPS = [
  {
    id: 1,
    shortLabel: "Empresa",
    title: "Dados cadastrais",
    description: "Confirme os dados da empresa e do responsável.",
    icon: BsFillBuildingFill,
  },
  {
    id: 2,
    shortLabel: "Crédito",
    title: "Condições da proposta",
    description: "Escolha o segmento e o valor solicitado.",
    icon: TbReportMoney,
  },
  {
    id: 3,
    shortLabel: "Documentos",
    title: "Checklist de upload",
    description: "Suba os documentos da empresa e dos sócios.",
    icon: FiFileText,
  },
  {
    id: 4,
    shortLabel: "SCR Bacen",
    title: "Autorização final",
    description: "Autorize a consulta e conclua a simulação.",
    icon: FiShield,
  },
];

const WIZARD_ACCENTS = {
  cyan: "wizard-stat-card--cyan",
  violet: "wizard-stat-card--violet",
  emerald: "wizard-stat-card--emerald",
  amber: "wizard-stat-card--amber",
  slate: "wizard-stat-card--slate",
};

function WizardStatCard({
  label,
  value,
  helper,
  icon: Icon,
  accent = "slate",
  dark = false,
}) {
  const themeClass = WIZARD_ACCENTS[accent] || WIZARD_ACCENTS.slate;

  return (
    <div
      className={[
        "wizard-stat-card",
        themeClass,
        dark ? "wizard-stat-card--dark" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="wizard-stat-card__bar" />
      <div className="wizard-stat-card__body">
        <div className="wizard-stat-card__content">
          <p className="wizard-stat-card__label">{label}</p>
          <div className="wizard-stat-card__value">{value}</div>
          {helper && <p className="wizard-stat-card__helper">{helper}</p>}
        </div>
        <span className="wizard-stat-card__icon">
          <Icon size={16} />
        </span>
      </div>
    </div>
  );
}

function WizardSectionCard({
  eyebrow,
  title,
  description,
  icon: Icon,
  children,
  action,
  className = "",
}) {
  return (
    <section className={`wizard-section-card ${className}`.trim()}>
      <div className="wizard-section-card__header">
        <div className="wizard-section-card__heading">
          <span className="wizard-section-card__icon">
            <Icon size={18} />
          </span>
          <div>
            <p className="wizard-section-card__eyebrow">{eyebrow}</p>
            <h3 className="wizard-section-card__title">{title}</h3>
            {description && (
              <p className="wizard-section-card__description">{description}</p>
            )}
          </div>
        </div>

        {action}
      </div>

      <div className="wizard-section-card__body">{children}</div>
    </section>
  );
}

function WizardStepCard({ step, activeStep, onClick }) {
  const isActive = step.id === activeStep;
  const isDone = step.id < activeStep;

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "wizard-step-card",
        isActive ? "wizard-step-card--active" : "",
        isDone ? "wizard-step-card--done" : "wizard-step-card--idle",
        onClick ? "wizard-step-card--clickable" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-current={isActive ? "step" : undefined}
    >
      <span className="wizard-step-card__index">
        {isDone ? <FiCheckCircle size={14} /> : step.id}
      </span>
      <div className="wizard-step-card__content">
        <p className="wizard-step-card__eyebrow">Etapa {step.id}</p>
        <div className="wizard-step-card__title">{step.shortLabel}</div>
      </div>
    </button>
  );
}

function WizardActionBar({
  backLabel = "Voltar",
  submitLabel = "Próximo",
  onBack,
  submitDisabled = false,
  isSubmitting = false,
  onBackDisabled = false,
  submitTone = "primary",
  footerText,
}) {
  return (
    <div className="wizard-action-bar">
      <div className="wizard-action-bar__inner">
        <p className="wizard-action-bar__text">{footerText}</p>
        <div className="wizard-action-bar__buttons">
          {onBack && (
            <button
              type="button"
              className="wizard-button wizard-button--secondary"
              onClick={onBack}
              disabled={onBackDisabled}
            >
              {backLabel}
            </button>
          )}
          <button
            type="submit"
            className={[
              "wizard-button",
              submitTone === "primary"
                ? "wizard-button--primary"
                : "wizard-button--emerald",
            ].join(" ")}
            disabled={submitDisabled || isSubmitting}
          >
            <span className="inline-flex items-center gap-2">
              {isSubmitting ? "Salvando..." : submitLabel}
              {!isSubmitting && <FiArrowRight size={14} />}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

function WizardProgressBar({ label, value, dark = false }) {
  return (
    <div
      className={["wizard-progress", dark ? "wizard-progress--dark" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="wizard-progress__head">
        <span className="wizard-progress__label text-white">{label}</span>
        <span className="wizard-progress__value text-white">{value}%</span>
      </div>
      <div className="wizard-progress__track">
        <div className="wizard-progress__fill" style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function WizardDocumentGroupCard({
  title,
  description,
  countLabel,
  progress,
  icon: Icon,
  children,
}) {
  return (
    <section className="wizard-doc-group">
      <div className="wizard-doc-group__header">
        <div className="wizard-doc-group__heading">
          <span className="wizard-doc-group__icon">
            <Icon size={18} />
          </span>
          <div>
            <p className="wizard-doc-group__eyebrow">Documentos</p>
            <h4 className="wizard-doc-group__title">{title}</h4>
            {description && (
              <p className="wizard-doc-group__description">{description}</p>
            )}
          </div>
        </div>

        <div className="wizard-doc-group__progress">
          <div className="wizard-doc-group__counter">{countLabel}</div>
          <div className="wizard-doc-group__track">
            <div
              className="wizard-doc-group__fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="wizard-doc-group__label">{progress}% concluído</p>
        </div>
      </div>
      <div className="wizard-doc-group__body">{children}</div>
    </section>
  );
}

function WizardUploadSlot({ children }) {
  return <div className="wizard-upload-slot">{children}</div>;
}

const FormPage = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [showAccessKeyModal, setShowAccessKeyModal] = useState(true);
  const [accessKeyInput, setAccessKeyInput] = useState("");
  const [accessKeyError, setAccessKeyError] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorSubmit, setErrorSubmit] = useState(false);
  const {
    register,
    getValues,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm();

  const {
    isLoading,
    error: errorLoan,
    data: loan,
    refetch,
  } = useQuery(
    ["loan"],
    async () => {
      const { data } = await axios.get(
        `${LOAN.GET_LOAN_BY_IDENTIFIER}/${id}?accessKey=${accessKeyInput}`,
      );

      setValue("company.name", data?.data?.Company?.name);
      setValue("company.document", data?.data?.Company?.document);
      setValue("company.responsibleName", data?.data?.Company?.responsibleName);
      setValue("company.phoneNumber", data?.data?.Company?.phoneNumber);
      setValue("company.email", data?.data?.Company?.email);
      setValue("company.cep", data?.data?.Company?.cep);
      setValue("company.address", data?.data?.Company?.address);
      setValue("company.addressNumber", data?.data?.Company?.addressNumber);
      setValue(
        "company.addressComplement",
        data?.data?.Company?.addressComplement,
      );
      setValue(
        "company.addressNeighborhood",
        data?.data?.Company?.addressNeighborhood,
      );
      setValue("company.addressCity", data?.data?.Company?.addressCity);
      setValue("credit.segmentTypeId", data?.data?.segmentTypeId);
      setValue("credit.amount", data?.data?.amount);
      setValue("credit.installments", data?.data?.installments);

      return data.data;
    },
    {
      enabled: false,
    },
  );

  const { data: segmentsData } = useQuery(["segments"], async () => {
    const { data } = await axios.get(LOAN.GET_SEGMENTS);
    return data.data;
  });

  useEffect(() => {
    if (
      loan?.segmentTypeId &&
      segmentsData?.length > 0 &&
      !watch("credit.category")
    ) {
      const foundSegment = segmentsData.find((segment) =>
        segment.SegmentTypes?.some((type) => type.id === loan.segmentTypeId),
      );

      if (foundSegment) {
        setValue("credit.category", foundSegment.id);
      }
    }
  }, [loan, segmentsData, setValue, watch]);

  const checkAccessKey = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.get(
        `${LOAN.GET_LOAN_BY_IDENTIFIER}/${id}?accessKey=${accessKeyInput}`,
      );

      if (data?.data) {
        setShowAccessKeyModal(false);
        setAccessKeyError(false);
        refetch();
      }
    } catch (err) {
      setAccessKeyError(true);
    }
  };

  const uploadedDocs = loan?.Documents || [];
  const isUploaded = (documentTypeId, partnerId = null) => {
    return uploadedDocs?.some(
      (doc) =>
        doc.documentTypeId === documentTypeId &&
        (doc.partnerId ?? null) === (partnerId ?? null),
    );
  };

  const countUploadedDocs = (appliesTo, partnerId = null) => {
    const total = documentTypes?.filter(
      (doc) => doc.appliesTo === appliesTo || doc.appliesTo === "both",
    )?.length;

    const uploaded = uploadedDocs?.filter(
      (doc) =>
        (doc.partnerId ?? null) === (partnerId ?? null) &&
        documentTypes?.some(
          (type) =>
            type.id === doc.documentTypeId &&
            (type.appliesTo === appliesTo || type.appliesTo === "both"),
        ),
    )?.length;

    return { total, uploaded };
  };

  const savePartialForm = async () => {
    try {
      setErrorSubmit(false);
      setIsSubmitting(true);
      const data = getValues();
      await axios.post(`${LOAN.UPLOAD_PARTIAL_FORM}/${id}`, data);
    } catch (error) {
      setErrorSubmit(true);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const {
    isLoading: isLoadingDocumentTypes,
    error: errorDocumentTypes,
    data: documentTypes,
  } = useQuery(["documentTypes"], async () => {
    const { data } = await axios.get(`${LOAN.GET_DOCUMENT_TYPES}`);

    return data.data;
  });

  const onSubmitCompany = async () => {
    try {
      await savePartialForm();
      setActiveStep(2);
    } catch {
      return;
    }
  };

  const onSubmitCredit = async () => {
    try {
      await savePartialForm();
      setActiveStep(3);
    } catch {
      return;
    }
  };

  const onSubmitFinalization = async () => {
    try {
      await savePartialForm();
      setActiveStep(4);
    } catch {
      return;
    }
  };

  const areAllDocumentsUploaded = () => {
    const companyDocsOk = documentTypes
      ?.filter(
        (item) => item.appliesTo === "company" || item.appliesTo === "both",
      )
      ?.every((item) => isUploaded(item.id));

    const partnersDocsOk = loan?.Company?.Partners?.every((partner) => {
      return documentTypes
        ?.filter(
          (item) => item.appliesTo === "partner" || item.appliesTo === "both",
        )
        ?.every((item) => isUploaded(item.id, partner.id));
    });

    return companyDocsOk && partnersDocsOk;
  };

  const onSubmitDocuments = async () => {
    setActiveStep(4);
  };

  const getRejectionReason = (documentTypeId, partnerId = null) => {
    const doc = uploadedDocs
      ?.filter(
        (doc) =>
          doc.documentTypeId === documentTypeId &&
          (doc.partnerId ?? null) === (partnerId ?? null),
      )
      ?.find((doc) => doc.rejectionReason); // pega o que tem rejectionReason preenchido

    return doc?.rejectionReason || null;
  };

  const selectedCreditCategory = watch("credit.category");
  const selectedCreditSegmentType = watch("credit.segmentTypeId");
  const currentStepConfig =
    WIZARD_STEPS.find((step) => step.id === activeStep) || WIZARD_STEPS[0];
  const loanCompany = loan?.Company || {};
  const companyName = loanCompany.name || "Nome não informado";
  const companyDocument = loanCompany.document || "CNPJ não informado";
  const requestedAmount =
    loan?.amount != null
      ? Number(loan.amount).toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })
      : "—";
  const companyDocs = documentTypes
    ? documentTypes.filter(
        (item) => item.appliesTo === "company" || item.appliesTo === "both",
      )
    : [];
  const partnerDocs = documentTypes
    ? documentTypes.filter(
        (item) => item.appliesTo === "partner" || item.appliesTo === "both",
      )
    : [];
  const overallDocumentSummary = (() => {
    if (!documentTypes || !loan) {
      return { total: 0, uploaded: 0, progress: 0 };
    }

    let total = 0;
    let uploaded = 0;

    companyDocs.forEach((doc) => {
      total += 1;
      if (isUploaded(doc.id)) uploaded += 1;
    });

    loanCompany?.Partners?.forEach((partner) => {
      partnerDocs.forEach((doc) => {
        total += 1;
        if (isUploaded(doc.id, partner.id)) uploaded += 1;
      });
    });

    return {
      total,
      uploaded,
      progress: total > 0 ? Math.round((uploaded / total) * 100) : 0,
    };
  })();
  const stepProgress = Math.round((activeStep / WIZARD_STEPS.length) * 100);
  const selectedSegmentName = selectedCreditSegmentType
    ? segmentsData
        ?.find((segment) => segment.id === selectedCreditCategory)
        ?.SegmentTypes?.find((type) => type.id === selectedCreditSegmentType)
        ?.name || "Tipo selecionado"
    : "Selecione um tipo";

  return (
    <section className="form-page">
      <div className="form-page__backdrop form-page__backdrop--glow" />
      <div className="form-page__backdrop form-page__backdrop--image" />
      <div className="form-page__backdrop form-page__backdrop--veil" />
      <div className="form-page__shell">
        {!showAccessKeyModal && (
          <div className="form-page__workspace">
            <header className="form-page__hero">
              <div className="form-page__hero-copy">
                <div className="form-page__hero-badge">
                  <FiShield size={12} />
                  Simulação segura
                </div>
                <h1 className="form-page__hero-title">
                  Preencha a simulação em passos simples
                </h1>
                <p className="form-page__hero-description">
                  Organizamos tudo para ficar mais fácil para o cliente:
                  primeiro os dados essenciais, depois crédito, documentos e
                  finalização.
                </p>
              </div>

              <div className="form-page__hero-stats">
                <WizardStatCard
                  label="Empresa"
                  value={companyName}
                  helper={companyDocument}
                  icon={BsFillBuildingFill}
                  accent="cyan"
                  dark
                />
                <WizardStatCard
                  label="Crédito"
                  value={requestedAmount}
                  helper={
                    loan?.installments
                      ? `${loan.installments} parcelas`
                      : "Parcela não definida"
                  }
                  icon={TbReportMoney}
                  accent="emerald"
                  dark
                />
              </div>
            </header>

            <section className="form-page__step-strip">
              <div className="form-page__step-strip-head">
                <div>
                  <p className="form-page__stage-eyebrow font-semibold">
                    Etapa atual
                  </p>
                </div>
                <span className="form-page__stage-badge">{activeStep}/4</span>
              </div>

              <div className="form-page__step-strip-grid">
                {WIZARD_STEPS.map((step) => (
                  <WizardStepCard
                    key={step.id}
                    step={step}
                    activeStep={activeStep}
                    onClick={() => setActiveStep(step.id)}
                  />
                ))}
              </div>
            </section>

            <div className="form-page__content">
              {(isLoading || isLoadingDocumentTypes) && (
                <div className="form-page__content-loading">
                  <DrawerLoading />
                </div>
              )}
              {errorSubmit || errorLoan || errorDocumentTypes ? (
                <div className="form-page__alert">
                  <span className="form-page__alert-title">Atenção.</span> Não
                  foi possível carregar ou salvar parte das informações. Tente
                  novamente.
                </div>
              ) : null}

              {activeStep === 1 && (
                <form
                  className="space-y-5"
                  onSubmit={handleSubmit(onSubmitCompany)}
                >
                  <WizardSectionCard
                    eyebrow="Confirmação"
                    title="Informações da empresa"
                    icon={BsFillBuildingFill}
                  >
                    <div className="grid gap-4 lg:grid-cols-2">
                      <CNPJInput
                        register={register}
                        name="company.document"
                        label="CNPJ"
                        control={control}
                      />
                      <Input
                        register={register}
                        name="company.name"
                        label="Razão Social"
                      />
                    </div>
                  </WizardSectionCard>

                  <WizardSectionCard
                    eyebrow="Contato"
                    title="Responsável e comunicação"
                    icon={FiUser}
                  >
                    <div className="grid gap-4 lg:grid-cols-3">
                      <Input
                        register={register}
                        name="company.responsibleName"
                        label="Nome do responsável"
                      />
                      <PhoneInput
                        control={control}
                        name="company.phoneNumber"
                        label="Telefone com DDD"
                        error={errors?.company?.phoneNumber}
                      />
                      <Input
                        register={register}
                        type="email"
                        name="company.email"
                        label="E-mail"
                      />
                    </div>
                  </WizardSectionCard>

                  <WizardSectionCard
                    eyebrow="Localização"
                    title="Endereço completo"
                    icon={FiMapPin}
                  >
                    <div className="grid gap-4 md:grid-cols-3">
                      <Input
                        register={register}
                        name="company.cep"
                        label="CEP"
                      />
                      <Input
                        register={register}
                        name="company.address"
                        label="Endereço"
                      />
                      <Input
                        register={register}
                        name="company.addressNumber"
                        label="Número"
                      />
                    </div>
                    <div className="mt-4 grid gap-4 md:grid-cols-3">
                      <Input
                        register={register}
                        name="company.addressComplement"
                        label="Complemento"
                      />
                      <Input
                        register={register}
                        name="company.addressNeighborhood"
                        label="Bairro"
                      />
                      <Input
                        register={register}
                        name="company.addressCity"
                        label="Cidade"
                      />
                    </div>
                  </WizardSectionCard>

                  <WizardActionBar
                    submitLabel="Próximo"
                    isSubmitting={isSubmitting}
                    footerText="As informações serão salvas parcialmente antes de avançar."
                  />
                </form>
              )}

              {activeStep === 2 && (
                <form
                  className="space-y-5"
                  onSubmit={handleSubmit(onSubmitCredit)}
                >
                  <WizardSectionCard
                    eyebrow="Segmento"
                    title="Escolha a categoria da operação"
                    icon={TbReportMoney}
                  >
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {segmentsData?.map((segment) => {
                        const isSelected =
                          String(selectedCreditCategory) === String(segment.id);

                        return (
                          <button
                            key={segment.id}
                            type="button"
                            className={`flex items-start gap-3 rounded-[24px] border p-4 text-left transition ${
                              isSelected
                                ? "border-slate-900 bg-slate-900 text-white shadow-lg"
                                : "border-slate-200 bg-slate-50/80 hover:-translate-y-0.5 hover:bg-white hover:shadow-sm"
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              setValue("credit.category", segment.id);
                              setValue("credit.segmentTypeId", null);
                            }}
                          >
                            <span
                              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
                                isSelected
                                  ? "bg-white/10 text-white"
                                  : "bg-slate-900 text-white"
                              }`}
                            >
                              {segment.name === "Capital de Giro" ? (
                                <TbReportMoney fontSize={18} />
                              ) : (
                                <GiFarmTractor fontSize={18} />
                              )}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div
                                className={`text-sm font-semibold ${
                                  isSelected ? "text-white" : "text-slate-900"
                                }`}
                              >
                                {segment.name}
                              </div>
                              <p
                                className={`mt-1 text-xs ${
                                  isSelected
                                    ? "text-white/70"
                                    : "text-slate-500"
                                }`}
                              >
                                {segment.SegmentTypes?.length || 0} opções
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </WizardSectionCard>

                  {selectedCreditCategory && (
                    <WizardSectionCard
                      eyebrow="Tipos"
                      title={`Tipos em ${segmentsData?.find((seg) => seg.id === selectedCreditCategory)?.name || "este segmento"}`}
                      icon={FaWandMagicSparkles}
                    >
                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {segmentsData
                          ?.find(
                            (seg) =>
                              String(seg.id) === String(selectedCreditCategory),
                          )
                          ?.SegmentTypes?.map((type) => {
                            const isSelected =
                              String(selectedCreditSegmentType) ===
                              String(type.id);

                            return (
                              <button
                                key={type.id}
                                type="button"
                                className={`rounded-[22px] border p-4 text-left transition ${
                                  isSelected
                                    ? "border-slate-900 bg-slate-900 text-white shadow-lg"
                                    : "border-slate-200 bg-slate-50/70 hover:bg-white hover:shadow-sm"
                                }`}
                                onClick={() =>
                                  setValue("credit.segmentTypeId", type.id)
                                }
                              >
                                <div
                                  className={`text-sm font-semibold ${
                                    isSelected ? "text-white" : "text-slate-900"
                                  }`}
                                >
                                  {type.name}
                                </div>
                              </button>
                            );
                          })}
                      </div>
                    </WizardSectionCard>
                  )}

                  <WizardSectionCard
                    eyebrow="Valores"
                    title="Condições financeiras"
                    icon={TbReportMoney}
                  >
                    <div className="grid gap-4 md:grid-cols-2">
                      <CurrencyInput
                        register={register}
                        label="Quanto deseja emprestar?"
                        control={control}
                        name="credit.amount"
                      />
                      <SelectInput
                        register={register}
                        label="Em quantas parcelas?"
                        name="credit.installments"
                      >
                        {Array.from({ length: 20 })?.map((_, index) => (
                          <option key={index} value={index + 1}>
                            {index + 1}
                          </option>
                        ))}
                      </SelectInput>
                    </div>
                  </WizardSectionCard>

                  <div className="form-page__summary-strip">
                    <div className="form-page__summary-item">
                      <span>Categoria</span>
                      <strong>
                        {selectedCreditCategory
                          ? segmentsData?.find(
                              (seg) => seg.id === selectedCreditCategory,
                            )?.name || "Selecione"
                          : "Não selecionada"}
                      </strong>
                    </div>
                    <div className="form-page__summary-item">
                      <span>Tipo</span>
                      <strong>{selectedSegmentName}</strong>
                    </div>
                    <div className="form-page__summary-item">
                      <span>Parcelas</span>
                      <strong>{watch("credit.installments") || "—"}</strong>
                    </div>
                  </div>

                  <WizardActionBar
                    backLabel="Voltar"
                    onBack={() => setActiveStep(1)}
                    submitLabel="Salvar e continuar"
                    isSubmitting={isSubmitting}
                    footerText="O crédito será salvo parcialmente antes de seguir."
                  />
                </form>
              )}

              {activeStep === 3 && (
                <form
                  className="space-y-5"
                  onSubmit={handleSubmit(onSubmitDocuments)}
                >
                  <WizardSectionCard
                    eyebrow="Checklist"
                    title="Documentos necessários"
                    icon={FiFileText}
                  >
                    <div className="form-page__document-progress">
                      <div className="form-page__document-progress-copy">
                        <span>
                          {overallDocumentSummary.uploaded}/
                          {overallDocumentSummary.total} enviados
                        </span>
                        <strong>
                          {overallDocumentSummary.progress}% concluído
                        </strong>
                      </div>
                      <div className="form-page__document-progress-track">
                        <div
                          className="form-page__document-progress-fill"
                          style={{
                            width: `${overallDocumentSummary.progress}%`,
                          }}
                        />
                      </div>
                    </div>
                  </WizardSectionCard>

                  <WizardDocumentGroupCard
                    title="Documentos da empresa"
                    countLabel={`${countUploadedDocs("company").uploaded}/${countUploadedDocs("company").total}`}
                    progress={
                      countUploadedDocs("company").total
                        ? Math.round(
                            (countUploadedDocs("company").uploaded /
                              countUploadedDocs("company").total) *
                              100,
                          )
                        : 0
                    }
                    icon={FiHome}
                  >
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {companyDocs.map((item) => (
                        <WizardUploadSlot key={item.id}>
                          <FileUploader
                            id={`file-${item.id}`}
                            label={item.name}
                            uploaded={isUploaded(item.id)}
                            onUpload={async (file) => {
                              const formData = new FormData();
                              formData.append("file", file);
                              formData.append("documentTypeId", item.id);
                              formData.append("documentName", item.name);

                              await axios.post(
                                `${LOAN.UPLOAD_LOAN_DOCUMENT}/${id}`,
                                formData,
                                {
                                  headers: {
                                    "Content-Type": "multipart/form-data",
                                  },
                                },
                              );

                              queryClient.setQueryData(["loan"], (old) => {
                                if (!old) return old;

                                const updatedDocuments = old.Documents.map(
                                  (doc) => {
                                    if (doc.documentTypeId === item.id) {
                                      return {
                                        ...doc,
                                        rejectionReason: null,
                                      };
                                    }
                                    return doc;
                                  },
                                );

                                return {
                                  ...old,
                                  Documents: updatedDocuments,
                                };
                              });

                              queryClient.invalidateQueries(["loan"]);
                              queryClient.invalidateQueries(["loan"]);
                            }}
                            rejectionReason={
                              getRejectionReason(item.id) || null
                            }
                          />
                        </WizardUploadSlot>
                      ))}
                    </div>
                  </WizardDocumentGroupCard>

                  {loanCompany?.Partners?.map((partner) => (
                    <WizardDocumentGroupCard
                      key={partner.id}
                      title={`Documentos de ${partner.name?.toLowerCase()}`}
                      countLabel={`${countUploadedDocs("partner", partner.id).uploaded}/${countUploadedDocs("partner", partner.id).total}`}
                      progress={
                        countUploadedDocs("partner", partner.id).total
                          ? Math.round(
                              (countUploadedDocs("partner", partner.id)
                                .uploaded /
                                countUploadedDocs("partner", partner.id)
                                  .total) *
                                100,
                            )
                          : 0
                      }
                      icon={FiUsers}
                    >
                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {partnerDocs.map((item) => (
                          <WizardUploadSlot key={`${item.id}-p-${partner.id}`}>
                            <FileUploader
                              id={`file-${item.id}-partner-${partner.id}`}
                              label={item.name}
                              uploaded={isUploaded(item.id, partner.id)}
                              onUpload={async (file) => {
                                const formData = new FormData();
                                formData.append("file", file);
                                formData.append("documentTypeId", item.id);
                                formData.append("partnerId", partner.id);
                                formData.append("documentName", item.name);

                                await axios.post(
                                  `${LOAN.UPLOAD_LOAN_DOCUMENT}/${id}`,
                                  formData,
                                  {
                                    headers: {
                                      "Content-Type": "multipart/form-data",
                                    },
                                  },
                                );

                                queryClient.setQueryData(["loan"], (old) => {
                                  if (!old) return old;

                                  const updatedDocuments = old.Documents.map(
                                    (doc) => {
                                      if (
                                        doc.documentTypeId === item.id &&
                                        (doc.partnerId ?? null) ===
                                          (partner?.id ?? null)
                                      ) {
                                        return {
                                          ...doc,
                                          rejectionReason: null,
                                        };
                                      }
                                      return doc;
                                    },
                                  );

                                  return {
                                    ...old,
                                    Documents: updatedDocuments,
                                  };
                                });

                                queryClient.invalidateQueries(["loan"]);
                              }}
                              rejectionReason={
                                getRejectionReason(item.id, partner.id) || null
                              }
                            />
                          </WizardUploadSlot>
                        ))}
                      </div>
                    </WizardDocumentGroupCard>
                  ))}

                  <WizardActionBar
                    backLabel="Voltar"
                    onBack={() => setActiveStep(2)}
                    submitLabel="Próximo"
                    isSubmitting={isSubmitting}
                    submitDisabled={isSubmitting || !areAllDocumentsUploaded()}
                    footerText={
                      areAllDocumentsUploaded()
                        ? "Tudo pronto para concluir a etapa de documentos."
                        : "Complete todos os uploads obrigatórios para avançar."
                    }
                  />
                </form>
              )}

              {activeStep === 4 && (
                <form
                  className="space-y-5"
                  onSubmit={handleSubmit(onSubmitFinalization)}
                >
                  <WizardSectionCard
                    eyebrow="SCR Bacen"
                    title="Revise e conclua"
                    icon={FiShield}
                  >
                    <div className="form-page__final-grid">
                      <div className="form-page__final-card">
                        <p className="form-page__final-copy">
                          Revise os pontos principais e conclua a autorização do
                          SCR.
                        </p>

                        <div className="form-page__final-points">
                          <span>Dados da empresa</span>
                          <span>Crédito e parcelas</span>
                          <span>Documentos enviados</span>
                        </div>

                        <div className="form-page__final-note">
                          Se algo estiver diferente, volte uma etapa e ajuste
                          antes de finalizar.
                        </div>
                      </div>

                      <div className="form-page__final-summary">
                        <div className="form-page__final-summary-row">
                          <span>Empresa</span>
                          <strong>{companyName}</strong>
                        </div>
                        <div className="form-page__final-summary-row">
                          <span>Crédito</span>
                          <strong>{requestedAmount}</strong>
                        </div>
                        <div className="form-page__final-summary-row">
                          <span>Documentos</span>
                          <strong>
                            {overallDocumentSummary.uploaded}/
                            {overallDocumentSummary.total}
                          </strong>
                        </div>
                        <div className="form-page__final-summary-row">
                          <span>Fluxo</span>
                          <strong>Etapa {activeStep}/4</strong>
                        </div>
                      </div>
                    </div>
                  </WizardSectionCard>

                  <WizardActionBar
                    backLabel="Voltar"
                    onBack={() => setActiveStep(3)}
                    submitLabel="Finalizar revisão"
                    submitTone="emerald"
                    isSubmitting={isSubmitting}
                    footerText="Ao finalizar, a proposta seguirá para a etapa de autorização SCR."
                  />
                </form>
              )}
            </div>
          </div>
        )}

        {showAccessKeyModal && (
          <section className="form-page__access-screen">
            <div className="wizard-modal__shell">
              <div className="wizard-modal__grid">
                <div className="wizard-modal__hero">
                  <div className="wizard-modal__hero-content">
                    <div className="wizard-modal__hero-badge">
                      <FiLock size={12} />
                      Acesso restrito
                    </div>
                    <h3 className="wizard-modal__hero-title">
                      Formulário de simulação de crédito
                    </h3>
                    <p className="wizard-modal__hero-copy">
                      Digite a chave recebida da equipe para liberar o acesso e
                      seguir com o preenchimento da proposta.
                    </p>
                  </div>
                </div>

                <div className="wizard-modal__form">
                  <div className="wizard-modal__form-head">
                    <div>
                      <p className="wizard-modal__form-eyebrow">
                        Passo inicial
                      </p>
                      <h4 className="wizard-modal__form-title">
                        Informe sua senha
                      </h4>
                    </div>
                    <span className="wizard-modal__lock">
                      <FiLock size={18} />
                    </span>
                  </div>

                  <form
                    onSubmit={checkAccessKey}
                    className="wizard-modal__form-body"
                  >
                    <div className="wizard-modal__input-group">
                      <label className="wizard-modal__label">
                        Senha de acesso
                      </label>
                      <input
                        type="password"
                        placeholder="Digite a chave enviada pela equipe"
                        value={accessKeyInput}
                        onChange={(e) => setAccessKeyInput(e.target.value)}
                        className="wizard-modal__input"
                      />

                      {accessKeyError && (
                        <div className="wizard-modal__error">
                          <GoAlertFill className="mt-0.5 shrink-0" />
                          <div>
                            <p className="wizard-modal__error-title">
                              Chave inválida
                            </p>
                            <p className="mt-1 text-sm leading-6">
                              Verifique a senha enviada pela equipe e tente
                              novamente.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="wizard-modal__info">
                      <p className="wizard-modal__info-title">
                        O que acontece depois
                      </p>
                      <div className="wizard-modal__info-list">
                        <div className="wizard-modal__info-row">
                          <FiCheckCircle className="mt-0.5 shrink-0 text-emerald-500" />
                          Liberação do formulário completo.
                        </div>
                        <div className="wizard-modal__info-row">
                          <FiCheckCircle className="mt-0.5 shrink-0 text-emerald-500" />
                          Carregamento automático das informações.
                        </div>
                      </div>
                    </div>

                    <button type="submit" className="wizard-modal__submit">
                      <span className="wizard-modal__submit-label">
                        Acessar formulário
                        <FiArrowRight size={14} />
                      </span>
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </section>
  );
};

export default FormPage;
